import { useEffect, useMemo, useRef, useState } from "react";
import type { TeamLivePosition } from "@/types";
import {
  flattenSegments,
  mergePositionsIntoTrail,
  parseTrail,
  serializeTrail,
  trailStorageKey,
  type TrailState,
} from "@/utils/teamLiveTrail";

const PERSIST_INTERVAL_MS = 10_000;

/**
 * Client-side breadcrumb accumulator for the no-layout team-live fallback
 * (see docs/superpowers/specs/2026-08-22-live-trail-map-design.md). Merges
 * each 1 s poll into per-member gap-separated segments and mirrors the state
 * to sessionStorage (throttled) so a page reload restores the trace. StrictMode
 * double-invocation is safe: re-merging the same poll is a no-op in the util
 * (duplicate-fix guard).
 */
export function useTeamLiveTrail(
  teamId: string | null,
  positions: TeamLivePosition[] | undefined,
  enabled: boolean,
) {
  const [state, setState] = useState<TrailState>({});

  // Restore once per team.
  useEffect(() => {
    if (!teamId) return;
    try {
      const raw = sessionStorage.getItem(trailStorageKey(teamId));
      if (raw !== null) setState(parseTrail(raw));
    } catch {
      // Storage blocked (private mode) — degrade to in-memory trail.
    }
  }, [teamId]);

  // Accumulate on every new poll result. React Query structural sharing keeps
  // the reference stable between unchanged polls, so this does not re-merge.
  // Callers may still pass a fresh array per render; merge always returns a
  // new object, so bail out when the content is unchanged — otherwise the
  // re-render → new array → re-merge cycle never settles.
  useEffect(() => {
    if (!enabled || !positions || positions.length === 0) return;
    setState((prev) => {
      const next = mergePositionsIntoTrail(prev, positions);
      return serializeTrail(next) === serializeTrail(prev) ? prev : next;
    });
  }, [enabled, positions]);

  // Throttled mirror to sessionStorage. Never write the empty state — on
  // mount this effect runs before the restore's setState lands and would
  // clobber the stored trail with {}.
  const lastWriteRef = useRef(0);
  useEffect(() => {
    if (!teamId || Object.keys(state).length === 0) return;
    const now = Date.now();
    if (now - lastWriteRef.current < PERSIST_INTERVAL_MS) return;
    lastWriteRef.current = now;
    try {
      sessionStorage.setItem(trailStorageKey(teamId), serializeTrail(state));
    } catch {
      // Quota exceeded — keep accumulating in memory.
    }
  }, [teamId, state]);

  const segments = useMemo(() => flattenSegments(state), [state]);
  return { segments };
}
